import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { ProjectService } from '../../../shared/services/project.service';
import { Project, ProjectStage, ProjectStatus, PROJECT_STATUS_OPTIONS } from '../../../shared/models/project.model';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TenantService } from '../../../shared/services/tenant.service';
import { Tenant } from '../../../shared/models/user.model';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { PreventScrollPropagationDirective } from '../../../shared/directives/prevent-scroll-propagation.directive';
import { firstValueFrom } from 'rxjs';

// Helper function to format date as YYYY-MM-DD in local timezone
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface StageEdit extends ProjectStage {
  startDateValue?: Date;
  endDateValue?: Date;
  _isNew?: boolean; // true if created in this edit session
}

@Component({
  selector: 'app-edit-project',
  imports: [FormsModule, CommonModule, DateInputsModule, PreventScrollPropagationDirective],
  templateUrl: './edit-project.html'
})
export class EditProjectComponent implements OnInit {
  private projectService = inject(ProjectService);
  private tenantService = inject(TenantService);

  @Input({ required: true }) project!: Project;
  @Output() close = new EventEmitter<void>();

  isSubmitting = false;
  currentStep = 1;
  tenants: Tenant[] = [];

  // Form data
  projectName = '';
  startDate = '';
  startDateValue: Date = new Date();
  tenantId = '';
  estimatedHours = 0;
  status: ProjectStatus = 'ACTIVE';

  readonly statusOptions = PROJECT_STATUS_OPTIONS;

  // Stages
  stages: StageEdit[] = [];
  private _originalStageIds = new Set<string>();
  private _deletedStageIds: string[] = [];

  // Default colors
  defaultColors = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#14b8a6'];

  async ngOnInit() {
    // Pre-fill from input project
    this.projectName = this.project.projectName;
    this.startDate = this.project.startDate || formatDateLocal(new Date());
    this.startDateValue = this.project.startDate
      ? new Date(this.project.startDate + 'T00:00:00')
      : new Date();
    this.tenantId = this.project.tenantId;
    this.estimatedHours = this.project.estimatedHours || 0;
    // A project saved before the status field existed reads as active.
    this.status = this.project.status ?? 'ACTIVE';

    // Load tenants
    this.tenants = await this.tenantService.listTenants();

    // Load existing stages for this project
    const allStages = await firstValueFrom(this.projectService.projectsStage$);
    this.stages = allStages
      .filter(s => s.projectId === this.project.projectId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map(s => ({
        ...s,
        startDateValue: s.startDate ? new Date(s.startDate + 'T00:00:00') : this.startDateValue,
        endDateValue: s.endDate ? new Date(s.endDate + 'T00:00:00') : this.startDateValue,
        _isNew: false
      }));
    this.stages.forEach(s => {
      if (s.projectStageId) this._originalStageIds.add(s.projectStageId);
    });
  }

  canGoToStep2(): boolean {
    return !!this.projectName.trim() && !!this.tenantId;
  }

  goToStep2() {
    if (this.canGoToStep2()) this.currentStep = 2;
  }

  goToStep1() {
    this.currentStep = 1;
  }

  onStartDateChange(value: Date | null) {
    if (value) {
      this.startDateValue = value;
      this.startDate = formatDateLocal(value);
    }
  }

  onStageStartDateChange(index: number, value: Date | null) {
    if (value && this.stages[index]) {
      this.stages[index].startDate = formatDateLocal(value);
      this.stages[index].startDateValue = value;
      if (this.stages[index].endDateValue && this.stages[index].endDateValue! < value) {
        this.stages[index].endDate = formatDateLocal(value);
        this.stages[index].endDateValue = new Date(value);
      }
    }
  }

  onStageEndDateChange(index: number, value: Date | null) {
    if (value && this.stages[index]) {
      const minDate = this.stages[index].startDateValue || this.startDateValue;
      if (value < minDate) value = minDate;
      this.stages[index].endDate = formatDateLocal(value);
      this.stages[index].endDateValue = value;
    }
  }

  addStage() {
    const colorIndex = this.stages.length % this.defaultColors.length;
    const startDate = new Date(this.startDateValue);
    const endDate = new Date(this.startDateValue);
    endDate.setDate(endDate.getDate() + 30);

    this.stages.push({
      projectId: this.project.projectId!,
      stageName: '',
      startDate: formatDateLocal(startDate),
      endDate: formatDateLocal(endDate),
      color: this.defaultColors[colorIndex],
      status: 'NOTBEGIN',
      estimatedHours: 0,
      startDateValue: startDate,
      endDateValue: endDate,
      _isNew: true
    });
  }

  removeStage(index: number) {
    const stage = this.stages[index];
    if (stage.projectStageId && this._originalStageIds.has(stage.projectStageId)) {
      this._deletedStageIds.push(stage.projectStageId);
    }
    this.stages.splice(index, 1);
  }

  getTotalPhaseHours(): number {
    return this.stages.reduce((sum, s) => sum + (s.estimatedHours || 0), 0);
  }

  isPhaseHoursOverBudget(): boolean {
    const budget = this.estimatedHours || 0;
    return budget > 0 && this.getTotalPhaseHours() > budget;
  }

  async onSubmit() {
    if (!this.projectName.trim()) {
      alert('Please enter a project name.');
      return;
    }
    if (!this.tenantId) {
      alert('Please select a tenant.');
      return;
    }

    this.isSubmitting = true;
    try {
      // 1. Update project fields
      const updates: Partial<Project> = {};
      if (this.projectName.trim() !== this.project.projectName) updates.projectName = this.projectName.trim();
      if (this.startDate !== this.project.startDate) updates.startDate = this.startDate;
      if (this.tenantId !== this.project.tenantId) updates.tenantId = this.tenantId;
      if (this.estimatedHours !== (this.project.estimatedHours || 0)) updates.estimatedHours = this.estimatedHours;
      if (this.status !== (this.project.status ?? 'ACTIVE')) {
        updates.status = this.status;
        // Same rule as setProjectStatus(): archiving stamps the date, anything
        // else clears it, so an unarchived project carries no stale timestamp.
        updates.archivedAt = this.status === 'ARCHIVED' ? new Date().toISOString() : null;
      }

      if (Object.keys(updates).length > 0) {
        await this.projectService.updateProject(this.project.projectId!, updates);
      }

      // 2. Delete removed stages
      for (const stageId of this._deletedStageIds) {
        await this.projectService.deleteProjectStage(stageId);
      }

      // 3. Update existing stages and create new ones
      for (let i = 0; i < this.stages.length; i++) {
        const stage = this.stages[i];
        if (stage._isNew) {
          // Create new stage
          await this.projectService.createProjectStage({
            projectId: this.project.projectId!,
            stageName: stage.stageName!,
            color: stage.color!,
            startDate: stage.startDate!,
            endDate: stage.endDate!,
            status: stage.status ?? 'NOTBEGIN',
            estimatedHours: stage.estimatedHours ?? 0,
            order: i
          });
        } else if (stage.projectStageId) {
          // Update existing stage
          await this.projectService.updateProjectStage(stage.projectStageId, {
            stageName: stage.stageName,
            color: stage.color,
            startDate: stage.startDate,
            endDate: stage.endDate,
            estimatedHours: stage.estimatedHours,
            order: i
          });
        }
      }

      this.close.emit();
    } catch (err: any) {
      console.error('Error updating project:', err);
      alert(err?.message || 'Error while updating project.');
    } finally {
      this.isSubmitting = false;
    }
  }
}
